import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react';

export function createArrayStore<StoreElement>() {
  const useStoreData = () => {
    const store = useRef([] as StoreElement[]);
    const subscribers = useRef(new Set<() => void>());

    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);
    const notify = useCallback(() => {
      subscribers.current.forEach((callback) => callback());
    }, []);
    const get = useCallback(() => store.current, []);
    const set = useCallback((value: StoreElement[]) => {
      store.current = value;
    }, []);

    return {
      subscribe,
      notify,
      get,
      set,
    };
  };
  type UseStoreDataReturnType = ReturnType<typeof useStoreData>;

  const StoreContext = createContext<UseStoreDataReturnType | null>(null);

  const StoreProvider = ({ children }: { children: ReactNode }) => {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    );
  };

  const getDataById = (arr: any[], id: string | null): StoreElement | null => {
    if (!id) return null;

    for (let i = 0; i < arr.length; i++) {
      if (arr[i].id === id) return arr[i];

      if (arr[i].props.children && arr[i].props.children.length) {
        const found = getDataById(arr[i].props.children, id);
        if (found) return found;
      }
    }

    return null;
  };

  const setDataById = (
    arr: any[],
    id: string | null,
    value: any
  ): StoreElement[] => {
    if (!id) return arr;

    return arr.map((item) => {
      if (item.id === id) {
        return { ...item, ...value };
      }

      if (item.props.children) {
        return {
          ...item,
          props: {
            ...item.props,
            children: setDataById(item.props.children, id, value),
          },
        };
      }

      return item;
    });
  };

  function useStore<T = any>({ id }: { id?: string | null } = {}): [
    T,
    (value: Partial<T> | ((prev: T) => Partial<T>)) => void,
  ] {
    const store = useContext(StoreContext)!;
    if (!store)
      throw new Error('Store not found. Check if you use it inside Provider');

    const getDataSnapshot = (id?: string | null) =>
      id ? getDataById(store.get(), id) : id === null ? null : store.get();

    const state = useSyncExternalStore(store.subscribe, () =>
      getDataSnapshot(id)
    );

    const setState = (value: Partial<T> | ((prev: T) => Partial<T>)) => {
      if (value instanceof Function)
        value = (value as (prev: T) => T)(getDataById(store.get(), id!) as T);
      const setData = id
        ? setDataById(store.get(), id, value)
        : id === null
        ? store.get()
        : value;
      const newState = setData;

      store.set(newState as unknown as StoreElement[]);
      store.notify();
    };

    return [state as T, setState];
  }

  return [StoreProvider, useStore] as const;
}
